import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
  type FC,
  type PropsWithChildren,
} from "react";
import { createPortal } from "react-dom";

type Placement = {
  target: HTMLDivElement | null;
  reviewActive: boolean;
  registerTarget: (id: string, target: HTMLDivElement | null) => void;
  registerReview: (id: string, mounted: boolean) => void;
};
const Context = createContext<Placement | undefined>(undefined);

/** Scopes a fee slot to one transaction: nested or adjacent transactions must
 * never display another transaction's reviewed quote. Product forms have no
 * slot (their fee stays with the button); the private action confirmation
 * shows its fee above its footer. */
export const PrivateFeePlacementProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const [targets, setTargets] = useState(
    () => new Map<string, HTMLDivElement>()
  );
  const [reviews, setReviews] = useState(() => new Set<string>());
  const registerTarget = useCallback(
    (id: string, target: HTMLDivElement | null) => {
      setTargets((previous) => {
        if (previous.get(id) === target || (!target && !previous.has(id)))
          return previous;
        const next = new Map(previous);
        if (target) next.set(id, target);
        else next.delete(id);
        return next;
      });
    },
    []
  );
  const registerReview = useCallback((id: string, mounted: boolean) => {
    setReviews((previous) => {
      if (previous.has(id) === mounted) return previous;
      const next = new Set(previous);
      if (mounted) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);
  const value = useMemo(
    () => ({
      // Responsive layouts can keep multiple overviews mounted. Ambiguous
      // destinations must never hide a review inside an inactive copy.
      target:
        targets.size === 1 && reviews.size === 1
          ? [...targets.values()][0]
          : null,
      reviewActive: reviews.size > 0,
      registerTarget,
      registerReview,
    }),
    [targets, reviews, registerTarget, registerReview]
  );
  return <Context.Provider value={value}>{children}</Context.Provider>;
};

/** Place under the same PrivateFeePlacementProvider as the transaction's
 * Confirm button. An empty slot occupies no space. Keep a slot in a stable
 * visible layout, or unmount it when hiding it using opacity/visibility alone
 * (those changes need not trigger ResizeObserver). */
export const PrivateFeeRowSlot: FC<{ className?: string }> = ({
  className,
}) => {
  const placement = useContext(Context);
  const id = useId();
  const registerTarget = placement?.registerTarget;
  const ref = useCallback(
    (target: HTMLDivElement | null) => registerTarget?.(id, target),
    [id, registerTarget]
  );
  if (!placement) return null;
  return (
    <div
      ref={ref}
      data-review-active={placement.reviewActive || undefined}
      className={`private-fee-slot${className ? ` ${className}` : ""}`}
    />
  );
};

/** Move only the rendering of the existing review, not its quote or approval.
 * Without a single visible slot the review stays inline, above its button. */
export const PrivateFeeDestination: FC<PropsWithChildren> = ({ children }) => {
  const placement = useContext(Context);
  const id = useId();
  const registerReview = placement?.registerReview;
  const target = placement?.target;
  const [visibility, setVisibility] = useState<{
    target?: HTMLDivElement | null;
    visible: boolean;
  }>({ visible: false });
  useEffect(() => {
    registerReview?.(id, true);
    return () => registerReview?.(id, false);
  }, [id, registerReview]);
  useEffect(() => {
    const check = () => {
      let visible = false;
      if (target?.isConnected) {
        // Read only the registered element. No DOM lookup, reparenting or
        // document-wide observer is needed to keep hidden fees inline.
        visible =
          typeof target.checkVisibility === "function"
            ? target.checkVisibility({
                checkOpacity: true,
                checkVisibilityCSS: true,
              })
            : target.getClientRects().length > 0 &&
              window.getComputedStyle(target).visibility !== "hidden";
      }
      setVisibility((previous) =>
        previous.target === target && previous.visible === visible
          ? previous
          : { target, visible }
      );
    };
    check();
    if (!target) return;
    const observer =
      typeof ResizeObserver === "function"
        ? new ResizeObserver(check)
        : undefined;
    observer?.observe(target);
    // Also cover direct hidden/class/style changes without observing an
    // unrelated tree. Ancestor display changes change this element's size.
    const mutation =
      typeof MutationObserver === "function"
        ? new MutationObserver(check)
        : undefined;
    mutation?.observe(target, {
      attributes: true,
      attributeFilter: ["hidden", "class", "style"],
    });
    window.addEventListener("resize", check);
    return () => {
      observer?.disconnect();
      mutation?.disconnect();
      window.removeEventListener("resize", check);
    };
  }, [target]);
  return target && visibility.target === target && visibility.visible ? (
    createPortal(children, target)
  ) : (
    <>{children}</>
  );
};
